import { Box } from '@mui/material'
import {
  dateHeureLisible,
  eanDeDocument,
  format,
  money,
  PAYMENT_METHOD_LABELS,
  referenceAffichable,
  soldeDocument,
  type CurrencyCode,
  type StoreMembership,
  type Transaction,
} from '@iobiz/shared'
import { Barcode } from '../../ui/Barcode.js'

/**
 * Ticket de caisse — le format ROULEAU, pas la facture A4.
 *
 * ⭐ Les deux ne servent pas la même chose. La facture se classe, se justifie,
 * s'envoie ; le ticket se remet en main propre au comptoir, sur une imprimante
 * thermique de 80 mm. Imprimer un tableau A4 sur un rouleau donne un document
 * coupé aux deux tiers — c'est ce qui se produisait ici.
 *
 * ⭐ Le CODE-BARRES en pied est ce qui rend le ticket utile après la vente :
 * un retour, un échange, une réclamation commencent par retrouver la vente, et
 * la scanner vaut mieux que de recopier « IN-2026-0008 » à la main.
 *
 * ⚠️ Tout en noir et sans niveaux de gris : une thermique n'a qu'une encre, et
 * un texte gris clair en sort illisible.
 */

const ENCRE = '#000'
/**
 * Largeur utile d'un rouleau 80 mm, marges de l'imprimante déduites.
 *
 * ⚠️ C'est aussi la largeur de PAGE demandée à l'impression, et ce n'est pas
 * un détail : le pilote d'une thermique 80 mm plafonne le papier à sa largeur
 * IMPRIMABLE — 72,07 mm sur la POS-90. Réclamer « 80mm » sortait du domaine
 * admis, le navigateur écartait la règle et retombait sur le papier par défaut
 * du pilote, 80 × 297 mm : le ticket partait sur une page haute comme une A4.
 */
export const LARGEUR_TICKET_MM = 72
const LARGEUR = `${LARGEUR_TICKET_MM}mm`

/**
 * Battement ajouté à la hauteur de page, en plus du contenu mesuré.
 *
 * ⚠️ La mise en page d'impression n'est pas au pixel près celle de l'écran où
 * l'on mesure. Sans ces quelques points, une dérive d'un seul pixel renverrait
 * la fin du ticket sur une seconde feuille.
 */
export const TOLERANCE_PAGE_MM = 3

/**
 * Blanc laissé en haut et sur les côtés du ticket.
 *
 * ⚠️ À L'INTÉRIEUR des 72 mm, jamais en plus : la largeur de page est plafonnée
 * par le pilote, et l'élargir la ferait écarter en bloc. D'où le
 * `box-sizing: border-box` explicite sur la racine — le laisser à l'héritage
 * exposerait le ticket à sortir en 78 mm le jour où une feuille de style
 * globale change d'avis.
 *
 * ⚠️ RIEN en bas : la zone sacrifiée s'en charge, et un blanc de plus ne
 * ferait qu'allonger la chute.
 */
export const MARGE_INTERNE_MM = 3

/**
 * Hauteur de la MARGE SACRIFIÉE qui ferme le ticket.
 *
 * ⚠️ Rien ne permet de dire à une thermique « le document s'arrête ici » : le
 * massicot tranche où il veut, et il tranche AVANT la fin. Trois campagnes
 * d'essais l'ont établi — la dernière avec une ligne « — Fin du document — »
 * posée tout en bas, qui est bel et bien sortie, mais sur la bande SUIVANTE.
 * Le document est donc imprimé en entier ; c'est la coupe qui tombe trop tôt.
 *
 * ⭐ D'où le montage qui ferme le composant : cette marge, PUIS un tiret. La
 * coupe tombe dans la marge au lieu de tomber dans le reçu, et le client ne
 * repart qu'avec un trait en bas de bande.
 *
 * ⚠️ La marge seule ne suffit pas : deux centimètres de vide terminal ont été
 * essayés, la coupe est tombée exactement au même endroit, juste sous la
 * signature. Il faut de l'ENCRE après elle pour que la fin du document tombe
 * plus bas — d'où le tiret.
 *
 * ⚠️ La mesure dépend aussi du NAVIGATEUR : Firefox pose le document en haut
 * de page, sans entête ni pied de métadonnées ; Chrome le centre et ajoute les
 * siens, ce qui ampute d'autant la zone utile. Cette réserve doit couvrir le
 * pire des deux.
 */
export const ZONE_SACRIFICE_MM = 35

function Filet({ double }: { double?: boolean }): JSX.Element {
  return (
    <Box
      sx={{
        borderTop: `1px ${double ? 'solid' : 'dashed'} ${ENCRE}`,
        margin: '4px 0',
      }}
    />
  )
}

function Ligne({
  gauche,
  droite,
  gras,
  grand,
}: {
  gauche: string
  droite: string
  gras?: boolean
  grand?: boolean
}): JSX.Element {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 2,
        fontWeight: gras ? 700 : 400,
        fontSize: grand ? '14px' : '11px',
      }}
    >
      <span>{gauche}</span>
      <span style={{ whiteSpace: 'nowrap' }}>{droite}</span>
    </Box>
  )
}

export function TicketCaisse({
  vente,
  boutique,
  rendu,
}: {
  vente: Transaction
  boutique: StoreMembership | undefined
  /** Monnaie rendue, quand le ticket suit un encaissement en espèces. */
  rendu?: number
}): JSX.Element {
  const devise = (boutique?.currencyCode ?? vente.currencyCode ?? 'XAF') as CurrencyCode
  const prix = (v: number): string => format(money(v, devise), 'fr-FR')
  const reference = referenceAffichable(vente)
  // ⚠️ Rien à imprimer tant que le numéro légal n'est pas attribué : le code
  // désignerait une référence provisoire vouée à changer.
  const ean = eanDeDocument(vente.docType, vente.number)

  return (
    <Box
      id="ticket"
      sx={{
        // Hors écran plutôt que masqué : un bloc en `display: none` ne
        // s'imprime pas du tout.
        position: 'absolute',
        left: '-200vw',
        top: 0,
        width: LARGEUR,
        boxSizing: 'border-box',
        padding: `${MARGE_INTERNE_MM}mm ${MARGE_INTERNE_MM}mm 0`,
        fontFamily: '"Helvetica Neue", Arial, sans-serif',
        fontSize: '11px',
        lineHeight: 1.35,
        color: ENCRE,
        background: '#fff',
        '& *': { color: ENCRE },
      }}
    >
      {/* ------------------------------------------------------------ entête */}
      <Box sx={{ textAlign: 'center' }}>
        <Box sx={{ fontSize: '15px', fontWeight: 700, textTransform: 'uppercase' }}>
          {boutique?.legalName || boutique?.name || 'Boutique'}
        </Box>
        {boutique?.address && <Box>{boutique.address}</Box>}
        {boutique?.phone && <Box>Tél. {boutique.phone}</Box>}
        {boutique?.taxId && <Box>N° fiscal {boutique.taxId}</Box>}
      </Box>

      <Filet double />

      <Ligne gauche={vente.docType === 'credit_note' ? 'AVOIR' : 'TICKET'} droite={reference} gras />
      <Ligne gauche="Date" droite={dateHeureLisible(vente.createdAt)} />
      {vente.cashierName && <Ligne gauche="Caisse" droite={vente.cashierName} />}
      <Ligne gauche="Client" droite={vente.customerName ?? 'Comptoir'} />

      <Filet />

      {/* ------------------------------------------------------------ lignes */}
      {vente.lines.map((l) => (
        <Box key={l.id} sx={{ marginBottom: '3px' }}>
          <Box sx={{ fontWeight: 600 }}>{l.label}</Box>
          <Ligne
            gauche={`${l.quantity} × ${prix(l.unitPrice)}${l.taxRate > 0 ? ` (TVA ${l.taxRate} %)` : ''}`}
            droite={prix(l.total)}
          />
        </Box>
      ))}

      <Filet />

      {/* ------------------------------------------------------------ totaux */}
      <Ligne gauche="Total HT" droite={prix(vente.subtotalAmount)} />
      {vente.discountAmount > 0 && (
        <Ligne gauche="Remise" droite={`- ${prix(vente.discountAmount)}`} />
      )}
      {/* ⚠️ La TVA s'affiche MÊME à zéro : sans ligne, rien ne distingue
          « article non taxé » d'un défaut d'impression. */}
      {vente.taxBreakdown.length === 0 ? (
        <Ligne gauche="TVA" droite={prix(0)} />
      ) : (
        vente.taxBreakdown.map((t) => (
          <Ligne key={t.rate} gauche={`TVA ${t.rate} %`} droite={prix(t.amount)} />
        ))
      )}

      <Filet double />
      <Ligne gauche="TOTAL TTC" droite={prix(vente.totalAmount)} gras grand />
      <Filet double />

      {/* ⭐ Le REMIS et le RENDU viennent du règlement lui-même, plus d'un
          calcul de passage. C'est ce qui rend le ticket REIMPRIMABLE : tiré
          depuis la fiche des semaines plus tard, il montre encore ce que le
          client a tendu et ce qu'on lui a rendu. Avant, ces deux lignes
          n'existaient que sur le tout premier tirage, et une contestation de
          monnaie ne pouvait plus être tranchée. */}
      {vente.payments.map((p) => (
        <Box key={p.id}>
          <Ligne gauche={PAYMENT_METHOD_LABELS[p.method]} droite={prix(p.amount)} />
          {p.tendered !== null && <Ligne gauche="Remis" droite={prix(p.tendered)} />}
          {p.change !== null && p.change > 0 && (
            <Ligne gauche="Rendu" droite={prix(p.change)} gras />
          )}
        </Box>
      ))}
      {/* Repli pour le tout premier tirage, quand la vente n'est pas encore
          relue de la réplique et ne porte donc pas encore ses colonnes. */}
      {rendu !== undefined && rendu > 0 && !vente.payments.some((p) => p.change !== null) && (
        <Ligne gauche="Rendu" droite={prix(rendu)} gras />
      )}
      {/* ⭐ Le solde, DANS LES DEUX SENS. Le ticket ne portait que la
          créance : quand la boutique restait devoir de l'argent — monnaie non
          rendue, trop-perçu — le client repartait avec un ticket qui n'en
          disait rien, et la dette ne tenait qu'à la mémoire du caissier.
          C'est pourtant sur le ticket qu'il reviendra la réclamer. */}
      {vente.docType !== 'quote' && soldeDocument(vente) !== 0 && (
        <Ligne
          gauche={soldeDocument(vente) > 0 ? 'RESTE DÛ' : 'AVOIR CLIENT'}
          droite={prix(Math.abs(soldeDocument(vente)))}
          gras
        />
      )}

      {!vente.number && (
        <>
          <Filet />
          <Box sx={{ fontSize: '9px' }}>
            Référence provisoire — {vente.docType === 'quote' ? 'devis établi' : 'vente enregistrée'}{' '}
            hors ligne. Le numéro définitif sera attribué au retour de la connexion.
          </Box>
        </>
      )}

      {vente.status === 'cancelled' && (
        <Box sx={{ marginTop: '6px', fontWeight: 700, textAlign: 'center' }}>
          *** VENTE ANNULÉE ***
        </Box>
      )}

      {/* ------------------------------------------------------------- queue */}
      {/* ⭐ Remerciement, code-barres et signature forment UN SEUL bloc, et
          non un pied de page. Posés à la suite les uns des autres, ils
          offraient au massicot autant d'endroits où trancher — et il
          tranchait, systématiquement, juste après le code-barres, quelle que
          soit la longueur du reçu. Le client repartait avec sa vente d'un
          côté et, de l'autre, une chute ne portant que « Merci… ». Deux
          tickets papier l'ont montré.

          ⚠️ Ce bloc ne protège que du NAVIGATEUR — qu'il ne répartisse pas
          ces trois lignes sur deux feuilles. Contre le MASSICOT, la parade est
          ailleurs : voir la zone sacrifiée en fin de composant.

          ⚠️ `page-break-*` DOUBLE `break-*` : les moteurs d'impression n'ont
          pas tous suivi le renommage, et ce qui compte ici n'est pas la
          finesse mais qu'aucun d'eux ne puisse détacher la signature. */}
      <Box
        sx={{
          marginTop: '10px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          breakInside: 'avoid',
          breakBefore: 'avoid',
          pageBreakInside: 'avoid',
          pageBreakBefore: 'avoid',
        }}
      >
        <Box sx={{ marginBottom: '6px' }}>Merci pour votre visite</Box>
        {/* ⭐ Un EAN-13, la MÊME symbologie que les articles en rayon : la
            boutique n'a qu'un lecteur, et lui présenter un autre encodage
            aurait obligé à ressaisir la référence à la main. */}
        {ean && <Barcode value={ean} height={34} moduleWidth={1.4} />}
        {/* ⭐ La signature de l'ÉDITEUR, dans les termes du pied de
            l'application : « Propulsé par eduqor ». Collée SOUS le
            code-barres, pas posée en fin de document : c'est ce qui la rend
            indissociable du reçu. */}
        <Box sx={{ marginTop: '2px', fontSize: '9px' }}>Propulsé par eduqor</Box>
      </Box>
      {/* ------------------------------------------------- zone sacrifiée */}
      {/* La marge que le massicot traverse, puis un tiret.

          ⚠️ Le tiret est nécessaire : une marge terminale ne déplace pas la
          coupe — deux centimètres de vide ont été essayés, elle est tombée au
          même endroit. Il faut de l'ENCRE après la marge pour que la fin du
          document tombe plus bas que le reçu. Du texte sans tracé n'y suffit
          pas non plus : sur une thermique, il ne produit aucun point. */}
      <Box aria-hidden>
        <Box sx={{ height: `${ZONE_SACRIFICE_MM}mm` }} />
        <Box sx={{ fontSize: '9px', textAlign: 'center' }}>-</Box>
      </Box>
    </Box>
  )
}
